/**
 * Domain contract for the refill-requirements feature (epic PROD-1264).
 *
 * The app's internal ("Refill") shape. It is adapted from the backend's
 * `MedicationRenewalResponse` (`GET /v1/medication-renewal/GLP1_WEIGHTLOSS`) in
 * `refillRequirementsApi.ts` — the FE keeps `Refill*` names; the rename to "Renewal"
 * is deferred.
 */

/**
 * Server-owned requirement types. Documented known-subset only — the backend set is
 * still growing, so `RefillRequirement.type` stays a plain `string` at the boundary and
 * the UI renders any value. Don't branch on these beyond fixtures/analytics.
 */
export enum RefillRequirementType {
  RECENT_WEIGH_IN = "RECENT_WEIGH_IN",
  WEIGH_IN_COUNT = "WEIGH_IN_COUNT",
  VITAL_COUNT = "VITAL_COUNT",
  PROGRAM_ACTIVITY_COUNT = "PROGRAM_ACTIVITY_COUNT",
  MODULE_COUNT = "MODULE_COUNT",
  LAB_ORDER_NOT_OVERDUE = "LAB_ORDER_NOT_OVERDUE",
  REFILL_CHECK_IN_SUBMITTED = "REFILL_CHECK_IN_SUBMITTED"
}

/**
 * Per-requirement display state (backend `RenewalRequirementResponse.state`, BACK-6069).
 * `LOCKED` means unfulfilled *and* not actionable yet — the backend refuses input until the
 * requirement's acceptance window opens, which is not always the renewal window itself (the
 * current-weight requirement opens 72h earlier). The unlock rule is clinical, so the FE never
 * derives this; it renders what the server sends.
 */
export const RefillRequirementState = {
  MET: "MET",
  UNMET: "UNMET",
  LOCKED: "LOCKED"
} as const;

/**
 * A union rather than an `enum` so the generated `RenewalRequirementResponse.state` assigns
 * straight into it: TS enums are nominal, so two enums with identical members stay
 * incompatible and the adapter would need a cast. Same idiom as `WsState`
 * (`src/constants/websocket.ts`).
 */
export type RefillRequirementState =
  (typeof RefillRequirementState)[keyof typeof RefillRequirementState];

/** One requirement as returned by the backend (`RequirementItemResponse`). */
export interface RefillRequirement {
  type: string;
  /** Base localized label; the FE appends "(count/minCount)" for countable requirements. */
  label: string;
  fulfilled: boolean;
  /** Current number of qualifying events; absent for non-countable requirements (e.g. labs). */
  count?: number;
  /** Events required to fulfill; absent for non-countable requirements. `fulfilled` once count >= minCount. */
  minCount?: number;
  /** When this fulfillment expires, if time-bounded. */
  expiresAt?: string | null;
  /**
   * Absent on payloads predating BACK-6069 and on responses cached before it shipped, so
   * "no state" reads as "not locked" rather than being defaulted at the boundary.
   */
  state?: RefillRequirementState;
}

/**
 * Domain shape the app renders. Adapted at the API boundary from the backend's
 * `MedicationRenewalResponse` (see `refillRequirementsApi.ts`) — the FE keeps its
 * `Refill*` vocabulary while the server speaks "renewal".
 */
export interface RefillRequirementsResponse {
  goalType: string;
  allFulfilled: boolean;
  completedCount: number;
  totalCount: number;
  /** Human-facing medication name (from the DTO); null if it can't be resolved. */
  medicationName?: string | null;
  /** Raw renewal status (ACTIVE_INTAKE, PENDING_SURVEY, PICKED_UP, …). Informational;
   * only the terminal statuses drive the concierge card's "completed → hidden". */
  status?: string;
  /** Window opens (renewal can start) — the concierge card's show-from edge. */
  windowOpensAt?: string | null;
  /** Target/anchor date shown to the member; once passed, the concierge card goes overdue. */
  dueAt?: string | null;
  /** Window ends — the backend's grace-period close, after which the renewal can no longer
   * complete. Carried through from the contract but read nowhere: since FEWEB-2710 the overdue
   * card deliberately outlives it. */
  windowClosesAt?: string | null;
  nextExpiresAt?: string | null;
  requirements: RefillRequirement[];
}

/** Whether the member gets their GLP-1 through 9am (concierge care) or externally. */
export enum RefillCohort {
  THROUGH_9AM = "THROUGH_9AM",
  OUTSIDE_9AM = "OUTSIDE_9AM"
}

/**
 * Home-screen refill card display state, derived cohort-first (see `getRefillCardVariant`).
 * - `windowOpen`: concierge "Yes", renewal window open, before the target date (purple).
 * - `overdue`: concierge "Yes", target date passed (orange). Stays until the renewal completes
 *   or the medication leaves the treatment plan — not until the window closes.
 * - `actionRequired`: concierge "No", requirements not all met (red).
 * - `secured`: concierge "No", all requirements met (green).
 * - `hidden`: no card.
 */
export type RefillCardVariant =
  | "windowOpen"
  | "overdue"
  | "actionRequired"
  | "secured"
  | "hidden";

export interface RefillRequirementsProgress {
  completed: number;
  total: number;
  /** 0–1; 0 when there are no requirements. */
  percent: number;
}

/** Countdown ring colour: green on track, orange running out, red once overdue. */
export type RefillCountdownTone = "green" | "orange" | "red";

/**
 * The hub hero countdown, derived in one pass (see `getHubCountdown`). `days` is whatever the
 * current reading counts — to the window opening, to the target, or past it — so the badge only
 * ever renders one number and one label.
 */
export interface RefillCountdown {
  days: number;
  /** 0–1 ring fill. */
  fraction: number;
  tone: RefillCountdownTone;
  overdue: boolean;
  beforeWindow: boolean;
}
